// next
import NextLink from 'next/link'
import { useRouter } from 'next/router'
// @mui
import { alpha, styled } from '@mui/material/styles'
import { Box, Card, Avatar, Typography, CardContent, Link, Stack } from '@mui/material'
// hooks
import useResponsive from '../../hooks/useResponsive'
// utils
import { fDate } from '../../utils/formatTime'
import { fShortenNumber } from '../../utils/formatNumber'
// @types
import { Post } from '../../@types/post'
// components
import Image from '../../components/Image'
import Iconify from '../../components/Iconify'
import TextMaxLine from '../../components/TextMaxLine'
import SvgIconStyle from '../../components/SvgIconStyle'
import TextIconLabel from '../../components/TextIconLabel'
import Label from '../../components/Label'

// ----------------------------------------------------------------------

const OverlayStyle = styled('div')(({ theme }) => ({
  top: 0,
  zIndex: 1,
  width: '100%',
  height: '100%',
  position: 'absolute',
  backgroundColor: alpha(theme.palette.grey[900], 0.8),
  transition: 'background-color 0.3s',
  ':hover': {
    backgroundColor: 'transparent',
  },
}))

// ----------------------------------------------------------------------

type Props = {
  post: Post
  index?: number
  showCategory?: boolean
  onClick?: (post: Post) => void
}

export default function BlogPostCard({ post, index, showCategory = true, onClick }: Props) {
  const isDesktop = useResponsive('up', 'md')
  const { push, locale } = useRouter()
  const { title, previewUrl, coverUrl, author, publishedAt, categories } = post

  const latestPost = index === 0 || index === 1 || index === 2

  const linkTo = `/post/${encodeURIComponent(title)}`

  const handleRouterPush = (path: string) => {
    onClick?.(post) ??
      push({ pathname: path }, undefined, {
        locale,
      })
  }

  if (isDesktop && latestPost) {
    return (
      <Card
        onClick={() => {
          handleRouterPush(linkTo)
        }}
        sx={{ cursor: 'pointer' }}
      >
        {showCategory && categories[0] && (
          <Label
            variant="filled"
            color="error"
            sx={{
              zIndex: 9,
              top: 16,
              right: 16,
              position: 'absolute',
            }}
          >
            {categories[0]}
          </Label>
        )}
        <Avatar
          alt={author.name}
          src={author.profileImage ?? ''}
          sx={{
            zIndex: 9,
            top: 24,
            left: 24,
            width: 40,
            height: 40,
            position: 'absolute',
          }}
        />
        <PostContent title={title} publishedAt={publishedAt} index={index} linkTo={linkTo} />
        <OverlayStyle />
        <Image alt="cover" src={previewUrl ?? coverUrl ?? ''} sx={{ height: 360 }} />
      </Card>
    )
  }

  return (
    <Card
      onClick={() => {
        handleRouterPush(linkTo)
      }}
      sx={{ cursor: 'pointer' }}
    >
      <Box sx={{ position: 'relative' }}>
        <SvgIconStyle
          src="https://minimal-assets-api.vercel.app/assets/icons/shape-avatar.svg"
          sx={{
            width: 80,
            height: 36,
            zIndex: 9,
            bottom: -15,
            position: 'absolute',
            color: 'background.paper',
          }}
        />
        {showCategory && categories[0] && (
          <Label
            variant="filled"
            color="error"
            sx={{
              zIndex: 9,
              top: 16,
              right: 16,
              position: 'absolute',
            }}
          >
            {categories[0]}
          </Label>
        )}
        <Avatar
          alt={author.name}
          src={author.profileImage ?? ''}
          sx={{
            left: 24,
            zIndex: 9,
            width: 32,
            height: 32,
            bottom: -16,
            position: 'absolute',
          }}
        />
        <Image alt="cover" src={previewUrl ?? coverUrl ?? ''} ratio="4/3" />
      </Box>

      <PostContent title={title} publishedAt={publishedAt} linkTo={linkTo} />
    </Card>
  )
}

// ----------------------------------------------------------------------

type PostContentProps = {
  title: string
  linkTo: string
  view?: number
  comment?: number
  share?: number
  publishedAt: string | null
  index?: number
}

export function PostContent({
  title,
  linkTo,
  view = 0,
  comment = 0,
  share = 0,
  publishedAt,
  index,
}: PostContentProps) {
  const isDesktop = useResponsive('up', 'md')

  const latestPostLarge = index === 0
  const latestPostSmall = index === 1 || index === 2

  const POST_INFO = [
    // { number: comment, icon: 'eva:message-circle-fill' },
    { number: view, icon: 'eva:eye-fill' },
    // { number: share, icon: 'eva:share-fill' },
  ]

  return (
    <CardContent
      sx={{
        pt: 4.5,
        width: 1,
        ...((latestPostLarge || latestPostSmall) && {
          pt: 0,
          zIndex: 9,
          bottom: 0,
          position: 'absolute',
          color: 'common.white',
        }),
      }}
    >
      <Typography
        gutterBottom
        variant="caption"
        component="div"
        sx={{
          color: 'text.disabled',
          ...((latestPostLarge || latestPostSmall) && {
            opacity: 0.64,
            color: 'common.white',
          }),
        }}
      >
        {publishedAt && fDate(publishedAt)}
      </Typography>

      <NextLink href={linkTo} passHref>
        <Link color="inherit" onClick={(e) => e.preventDefault()}>
          <TextMaxLine
            variant={isDesktop && latestPostLarge ? 'h5' : 'subtitle2'}
            line={2}
            persistent
          >
            {title}
          </TextMaxLine>
        </Link>
      </NextLink>

      <Stack
        flexWrap="wrap"
        direction="row"
        justifyContent="flex-end"
        sx={{
          mt: 3,
          color: 'text.disabled',
          ...((latestPostLarge || latestPostSmall) && {
            opacity: 0.64,
            color: 'common.white',
          }),
        }}
      >
        {/* {POST_INFO.map((info, index) => (
          <TextIconLabel
            key={index}
            icon={<Iconify icon={info.icon} sx={{ width: 16, height: 16, mr: 0.5 }} />}
            value={fShortenNumber(info.number)}
            sx={{ typography: 'caption', ml: index === 0 ? 0 : 1.5 }}
          />
        ))} */}
      </Stack>
    </CardContent>
  )
}
